<template>
  <compnent
    :is="href === '' ? tagName : 'a'"
    class="oct-button"
    :class="[
      {
        'oct-button--outlined': outline,
        'oct-button--with-help': true,
      },
      `oct-button--${type}`,
      `oct-button--${size}`,
      `oct-button--align-${align}`
    ]"
    :href="href === '' ? false : href"
  >
    <oct-icon
      class="oct-button__leading-icon"
      :icon="icon" v-if="icon"
    />

    {{ text }}
    <slot />

    <!-- Render help content if helper slot is sat -->
    <div
      class="oct-button-helper"
      v-if="$slots.helper"
      @mouseenter="openPopover(helperId, $event.currentTarget)"
      @mouseleave="closePopover(helperId)"
      @click="$event.stopPropagation()"
    >
      <!-- Help Icon -->
      <oct-icon
        class="oct-button-helper__icon"
        icon="help-sign"
      />

      <!-- Render popover -->
      <oct-popover
        :id="helperId"
      >
        <slot name="helper" />
      </oct-popover>
    </div>
  </compnent>
</template>

<script>
import OctIcon from '@/components/icon/OctIcon.vue';
import OctPopover, {useOctPopover}  from '@/components/popover/OctPopover.vue';

/**
 * Destructures the `open` and `close` methods from the `useOctPopover` composable.
 * 
 * @constant {Function} openPopover - Function to open the popover.
 * @constant {Function} closePopover - Function to close the popover.
 */
const {open: openPopover, close: closePopover} = useOctPopover();

export default {
  name: 'OctButton',
  components: {
    OctIcon,
    OctPopover
  },
  props: {
    /** Tag name */
    tagName: {
      type: String,
      default: 'button'
    },
    /** Button text */
    text: {
      type: String,
      default: ''
    },
    /** Href */
    href: {
      type: String,
      default: ''
    },
    /** Icon ID to display */
    icon: {
      type: String,
      default: null
    },
    /** Type of button [default | primary | danger | link | google | twitter | facebook | line | processing] */
    type: {
      type: String,
      default: 'default'
    },
    /** Text alignment [center | left | right] */
    align: {
      type: String,
      default: 'center'
    },
    /** Whether outlined */
    outline: {
      type: Boolean,
      default: false
    },
    /** Size [small, medium, large] */
    size: {
      type: String,
      default: 'medium'
    },
  },
  computed: {
    /** Generate random ID for the helper popover */
    helperId () {
      return `oct-button-helper--${Math.random().toString(36).substring(2)}`
    }
  },
  methods: {
    openPopover,
    closePopover
  } 
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.oct-button {
  font-family: $font-family-sans-serif;
  font-size: oct-rem(15);
  font-weight: 400;
  line-height: 1;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  color: $oct-theme--primary;
  position: relative;
  margin: 0;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: oct-rem(40);
  padding: 0 oct-rem(10-1.5);
  background-color: rgba(0,0,0,.05);
  border: 1.5px solid transparent;
  border-radius: oct-rem(20);
  outline: none;
  box-sizing: border-box;
  appearance: none;
  overflow: hidden;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0,0,0,0);
  transition-duration: .2s;

  &:hover, &--hover {
    color: $oct-theme--primary;
    text-decoration: none;
    background-color: rgba(0,0,0,.1);
  }

  /* Cancel hover style on the helper content */
  &:hover:has(.oct-button-helper:hover), &--hover:has(.oct-button-helper:hover) {
    background-color: rgba(0,0,0,.05);
  }

  &--align-left {
    justify-content: flex-start;
  }

  &--outlined {
    color: $oct-theme--primary;
    border-color: $oct-theme--primary;
    background-color: transparent;

    &:hover, &.oct-button--hover {
      color: $oct-theme--on-primary;
      background-color: $oct-theme--primary;
    }

    /* Cancel hover style on the helper content */
    &:hover:has(.oct-button-helper:hover), &--hover:has(.oct-button-helper:hover) {
      color: $oct-theme--primary;
      background-color: transparent;
    }
  }

  &--primary {
    color: $oct-theme--on-primary;
    background-color: $oct-theme--primary;
    border: 1.5px solid $oct-theme--primary;

    &:hover, &.oct-button--hover {
      color: $oct-theme--primary;
      background-color: transparent;
    }
  }

  &--danger {
    color: $oct-theme--on-primary;
    background-color: $oct-theme--danger;
    border: 1.5px solid $oct-theme--danger;

    &:hover, &.oct-button--hover {
      color: $oct-theme--on-primary;
      background-color: #D71920;
      border-color: transparent;
    }
  }

  &--on-primary {
    color: $oct-theme--primary;
    border-color: $oct-theme--on-primary;
    background-color: $oct-theme--on-primary;

    &:hover, &.oct-button--hover {
      color: $oct-theme--on-primary;
      background-color: $oct-theme--primary;
      border-color: $oct-theme--primary;
    }
  }

  &--on-primary.oct-button--outlined {
    color: $oct-theme--on-primary;
    border-color: $oct-theme--on-primary;
    background-color: transparent;

    &:hover, &.oct-button--hover {
      color: $oct-theme--primary;
      background-color: $oct-theme--on-primary;
      border-color: $oct-theme--on-primary;
    }
  }

  &--twitter,
  &--facebook,
  &--line {
    color: $oct-theme--on-primary;

    &:hover {
      color: rgba(255,255,255,.7);
    }
  }

  &--twitter {
    &, &:hover {
      background-color: #1DA1F2;
    }
  }

  &--facebook {
    &, &:hover {
      background-color: #1977F2;
    }
  }

  &--line {
    &, &:hover {
      background-color: #00CA41;
    }
  }

  &--google {
    &, &:hover {
      background-color: $oct-theme--on-primary;
    }

    &:hover {
      color: rgba(0,0,0,.7);
    }
  }

  &--link {
    border-color: transparent;
    background-color: transparent;

    &:hover {
      color: $oct-theme--primary;
      border-color: transparent;
      background-color: transparent;
    }
  }

  &[disabled] {
    color: $oct-theme--on-primary;
    background-color: rgba(0,0,0,.2);
    border-color: transparent;
    pointer-events: none;
  }

  &--block {
    width: 100%;
  }

  &--large {
    font-size: oct-rem(15);
    height: oct-rem(40);
    padding: 0 oct-rem(12-1.5);
    border-radius: oct-rem(20);
  }

  &--google,
  &--twitter,
  &--facebook,
  &--line {
    height: oct-rem(44);
    padding: 0 oct-rem(16);
    border-radius: oct-rem(22);
  }

  &--processing,
  &--processing[disabled],
  &--processing:hover,
  &--processing[disabled]:hover {
    position: relative;
    color: transparent;
    background-color: rgba(0,0,0,.05);
    border-color: transparent;
    pointer-events: none;

    &:after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      margin: auto;
      width: oct-rem(8);
      height: oct-rem(8);
      background-color: #000;
      border-radius: 50%;
      animation: blink .1s ease-out 0s infinite alternate;
    }
  }

  &--with-help {
    overflow: visible;
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(18);
  height: 36px;
  padding: 0 16px-4;
  border-width: 2px;
  border-radius: 18px;

  &--primary {
    padding: 0 14px;
  }

  &--small {
    font-size: oct-rem(15);
    height: 26px;
    padding: 0 9px;
    border-radius: 13px;
    border-width: 1px;
  }

  &--large {
    font-size: oct-rem(18);
    height: 48px;
    padding: 0 20px-4;
    border-radius: 24px;
  }
}
}

@keyframes blink {
  0% {opacity: 1}
  100% {opacity: 0}
}

.oct-button__leading-icon {
  margin: 0 oct-rem(6) 0 0;
  width: oct-rem(24);
  height: oct-rem(24);

  .oct-button:hover & {
    stroke: $oct-theme--on-primary;
  }

  .oct-button--link:hover & {
    stroke: $oct-theme--primary;
  }

  .oct-button--twitter &,
  .oct-button--facebook &,
  .oct-button--line & {
    fill: $oct-theme--on-primary;
  }

  .oct-button--google:hover &,
  .oct-button--twitter:hover &,
  .oct-button--facebook:hover &,
  .oct-button--line:hover & {
    stroke-width: 0;
  }

  .oct-button--large & {
    width: oct-rem(28);
    height: oct-rem(28);
  }
}

/* Helper Content */
.oct-button-helper {
  position: absolute;
  top: 50%;
  left: 100%;
  margin-left: oct-rem(8);
  display: none;
  cursor: default;
  transform: translateY(-50%);

  &:hover {
    // Update the z-index to ensure the popover is on top
    z-index: 1;
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  display: flex;
}
}

.oct-button-helper__icon {
  width: oct-rem(14);
  height: oct-rem(14);
  opacity: .4;
}

.oct-button-group {
  background-color: $oct-theme--surface;
  border-radius: 15px;

  .oct-button {
    border-bottom: .99px solid $oct-theme--secondary-border;
    border-radius: 0;

    &:first-child {
      border-top-right-radius: 15px;
      border-top-left-radius: 15px;
    }

    &:last-child {
      border-bottom-width: 0;
      border-bottom-right-radius: 15px;
      border-bottom-left-radius: 15px;
    }
  }

  .oct-button--square {
    &:first-child {
      border-top-right-radius: 15px;
      border-top-left-radius: 15px;
    }

    &:last-child {
      border-bottom-right-radius: 15px;
      border-bottom-left-radius: 15px;
    }
  }

  &--horizontal {
    display: inline-flex;
    flex-wrap: nowrap;

    .oct-button {
      flex: 1 1 auto;
      border-bottom-width: 0;
      border-right: .99px solid #e3e3e3;
    }

    .oct-button3 {
      border-radius: 0;
      background-color: transparent;
    }

    .oct-button3--link.oct-button3--dense {
      text-decoration: none;
    }

    .oct-button3--outlined {
      flex: 1 1 auto;
      border: .99px solid #e3e3e3;
      border-radius: 0;

      &:first-child {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
        border-right-width: 0;
      }

      &:last-child {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }
    }

    .oct-button3--link {
      flex: 1 1 auto;
      border-bottom-width: 0;
      border-right: .99px solid #e3e3e3;

      &:last-child {
        border-right-width: 0;
      }
    }

    .oct-button--square {
      &:first-child {
        border-top-right-radius: 0;
        border-bottom-left-radius: 15px;
      }

      &:last-child {
        border-width: 0;
        border-top-right-radius: 15px;
        border-bottom-left-radius: 0;
      }
    }
  }
}

.oct-button-group-header {
  color: $oct-theme--primary;
  padding: 20px;
  border-top-right-radius: 15px;
  border-top-left-radius: 15px;
  background-color: $oct-theme--surface;
  border-bottom: .99px solid $oct-theme--secondary-border;
}

.oct-button-group-header__icon {
  position: relative;
  margin: 0 auto 15px;
  display: block;
  width: 55px;
  height: 55px;
  border-radius: 50%;
  overflow: hidden;
}

.oct-button-group-header__image {
  position: absolute;
  top: -100%;
  right: -100%;
  bottom: -100%;
  left: -100%;
  margin: auto;
  height: 100%;
}

.oct-button-group-header__text {
  font-size: oct-rem(11);
  font-weight: 600;
  line-height: (16/11);
  text-align: center;
  margin: (-2em/16) auto (-3em/16);
  display: block;
  max-width: 225px;
}
</style>
